
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { SuccessHeader } from "./subscription/SuccessHeader";
import { ErrorNotification } from "./subscription/ErrorNotification";
import { RedirectCountdown } from "./subscription/RedirectCountdown";
import { ActionButtons } from "./subscription/ActionButtons";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";

// Reduced redirect delay for faster UX
const REDIRECT_DELAY = 8000;

const SubscriptionSuccess = () => {
  const { refreshProfile, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [error, setError] = useState<string | null>(null);
  const [showRedirectButton, setShowRedirectButton] = useState(false);
  const [showCountdown, setShowCountdown] = useState(false);
  const [isUpdating, setIsUpdating] = useState(true);
  const [checkProgress, setCheckProgress] = useState(10);
  const [verificationAttempted, setVerificationAttempted] = useState(false);

  // Extract subscription ID from URL if present
  const searchParams = new URLSearchParams(location.search);
  const subscriptionId = searchParams.get('subscription_id');

  useEffect(() => {
    // Verify subscription with PayPal instead of immediate activation
    const verifySubscriptionWithPayPal = async () => {
      if (verificationAttempted) {
        return; // Prevent multiple verification attempts
      }

      if (!user || !subscriptionId) {
        setError("Missing user information or subscription ID");
        setIsUpdating(false);
        setShowRedirectButton(true);
        return;
      }
      
      setVerificationAttempted(true);
      
      try {
        console.log("Verifying subscription with PayPal:", subscriptionId);
        
        // Call the subscription-status endpoint to verify subscription
        const { data, error: statusError } = await supabase.functions.invoke('subscription-status', {
          body: { 
            userId: user.id, 
            paypalSubscriptionId: subscriptionId,
            // We now explicitly verify with PayPal before activating
            verifyWithPayPal: true 
          }
        });
        
        if (statusError || !data?.success) {
          console.error("Error verifying subscription:", statusError || data?.error);
          setError(data?.error || "There was an issue verifying your subscription with PayPal");
          setIsUpdating(false);
          setShowRedirectButton(true);
          return;
        }
        
        // If verification was successful, update UI
        console.log("Subscription verification successful:", data);
        
        // Force refresh profile to get updated data
        await refreshProfile();
        
        // Show success message
        toast({
          title: "Subscription Verified!",
          description: "Your subscription has been confirmed with PayPal",
          variant: "default",
        });
        
        // Update UI
        setIsUpdating(false);
        setShowCountdown(true);
        setCheckProgress(100);
        setShowRedirectButton(true);
        
        // Start auto-redirect countdown
        setTimeout(() => {
          if (document.visibilityState !== 'hidden') {
            navigate("/dashboard");
          }
        }, REDIRECT_DELAY);
      } catch (err) {
        console.error("Error in subscription verification:", err);
        setError("There was an issue verifying your subscription. Please try refreshing the page.");
        setShowRedirectButton(true);
        setIsUpdating(false);
      }
    };

    verifySubscriptionWithPayPal();
    
    // Animate progress bar to show activity
    const interval = setInterval(() => {
      setCheckProgress(prev => {
        const newValue = prev + 5;
        return newValue > 90 ? 10 : newValue;
      });
    }, 700);
    
    return () => clearInterval(interval);
  }, [user, refreshProfile, navigate, toast, subscriptionId, verificationAttempted]);

  const handleManualRedirect = () => {
    navigate("/dashboard");
  };

  return (
    <div className="container max-w-md mx-auto py-16 px-4 text-center">
      <SuccessHeader />
      
      {isUpdating && (
        <div className="mb-8">
          <Progress value={checkProgress} className="h-2 mb-2" />
          <p className="text-xs text-gray-500">Verifying your subscription...</p>
        </div>
      )}
      
      <ErrorNotification error={error} />
      
      {showCountdown && (
        <RedirectCountdown seconds={Math.floor(REDIRECT_DELAY / 1000)} />
      )}
      
      <ActionButtons
        showRedirectButton={showRedirectButton}
        isUpdating={isUpdating}
        error={error}
        onManualRedirect={handleManualRedirect}
      />
    </div>
  );
};

export default SubscriptionSuccess;
